import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  BrowsePackageTourIcon,
  BusinessCenterIcon,
  DesignBespokeTourIcon,
  DirectionsCarIcon,
  LocalActivityIcon,
  PlaneIcon,
  RequestLuxuryJourneyIcon,
  TrainIcon,
} from "../../../../components/icons";
import {
  fadeSide,
  Reveal,
  Stagger,
  StaggerItem,
} from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { buildLocalizedPath, scrollToSection } from "../navigation";
import {
  getImageFocusStyle,
  IMAGE_FOCUS_CLASS,
  SERVICE_IMAGES,
} from "../serviceImages";
import {
  AsideNote,
  BlockHeading,
  ExpandableNote,
  ImagePair,
  InfoCard,
  LabeledSplit,
  QuickLinkCard,
  RichText,
  SectionHeading,
  ServiceHeroCard,
  WIDE_COLUMN_CLASS,
} from "./ui";

/** Anchor ids so the home page and the jump cards can reach a sub-service. */
export const CHAUFFEUR_SECTION_IDS = {
  airportTransfer: "airport-transfer",
  businessTransfer: "business-transfer",
  privateTour: "private-tour",
};

/** Every small glyph on the cards is 16px and the same dark grey in Figma. */
const ICON_COLOR = "#4E4E4E";
const ICON_SIZE = 16;

const TRANSFER_JUMP_LINKS = [
  {
    id: "AirportTransfer",
    sectionId: CHAUFFEUR_SECTION_IDS.airportTransfer,
    icon: <PlaneIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "BusinessTransfer",
    sectionId: CHAUFFEUR_SECTION_IDS.businessTransfer,
    icon: <BusinessCenterIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "PrivateTour",
    sectionId: CHAUFFEUR_SECTION_IDS.privateTour,
    icon: <LocalActivityIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
];

/** The airport maps page opens on the tab named by its `type` parameter. */
const PICKUP_POINT_LINKS = [
  {
    id: "Haneda",
    mapTab: "haneda-airport",
    icon: <PlaneIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "Narita",
    mapTab: "narita-airport",
    icon: <PlaneIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "Shinagawa",
    mapTab: "shinagawa-station",
    icon: <TrainIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
];

const AIRPORT_INFO_BLOCKS = [
  "CorporateWelcome",
  "MultilingualSupport",
  "SeamlessService",
];

const CONCIERGE_NOTES = ["TravelArrangement", "ThoughtfulExtras"];

const PRIVATE_TOUR_LINKS = [
  {
    id: "DesignBespokeTour",
    href: "https://www.japanprivatetour.com/design-a-bespoke-tour",
    icon: <DesignBespokeTourIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "RequestLuxuryJourney",
    href: "https://www.japanprivatetour.com/luxury-experiences",
    icon: <RequestLuxuryJourneyIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
  {
    id: "BrowsePackageTours",
    href: "https://www.japanprivatetour.com/package-tours",
    icon: <BrowsePackageTourIcon color={ICON_COLOR} size={ICON_SIZE} />,
  },
];

/** Left/right page margin: 82px on desktop, as in the design. */
const PAGE_GUTTER_CLASS = "tw:px-5 tw:lg:px-[82px]";

/** 15px / 22px body copy ("Paragraph" in the Figma styles). */
const BODY_TEXT_CLASS =
  "tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[14px] tw:lg:text-[15px] tw:leading-[20px] tw:lg:leading-[22px]";

/** 60px between the blocks inside a section. */
const BLOCK_GAP_CLASS = "tw:mt-10 tw:lg:mt-[60px]";

/**
 * A hairline across the content with 60px above and below it. The rule has
 * no height in Figma, so its 1px comes out of the space below.
 */
const DIVIDED_BLOCK_CLASS = cn(
  BLOCK_GAP_CLASS,
  "tw:pt-10 tw:lg:pt-[59px] tw:border-t tw:border-t-[#F1F1F1]"
);

/**
 * Every sub-service sits below a hairline with 72px of space on either side.
 * The rule stays inside the page margins, as in the design.
 */
const ServiceSection = ({ id, children }) => (
  <section id={id} className={cn("tw:scroll-mt-[120px]", PAGE_GUTTER_CLASS)}>
    <div className="tw:border-t tw:border-t-[#F1F1F1] tw:py-10 tw:lg:pt-[71px] tw:lg:pb-[72px]">
      {children}
    </div>
  </section>
);

/**
 * A photo that fills the height of the copy next to it on desktop (Figma
 * stretches it to the row) and is a fixed 200px band on mobile.
 */
const StretchedSidePhoto = ({ image, alt }) => (
  // `fadeSide` slides the photo in from its own side of the row.
  <Reveal
    variants={fadeSide(28)}
    className="tw:relative tw:h-[200px] tw:lg:h-auto tw:lg:flex-1 tw:lg:self-stretch tw:rounded-[8px] tw:overflow-hidden"
  >
    <img
      src={image.src}
      style={getImageFocusStyle(image)}
      alt={alt}
      className={cn(
        "tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover",
        IMAGE_FOCUS_CLASS
      )}
    />
  </Reveal>
);

/** The row that pairs a block of copy with a photo on its right. */
const COPY_WITH_PHOTO_ROW_CLASS =
  "tw:flex tw:flex-col tw:lg:flex-row tw:gap-6 tw:lg:gap-[68px]";

export const Chauffeur = () => {
  const { t, i18n } = useTranslation();

  return (
    <div>
      {/* Lead: what the tab is about, plus the small print beside it */}
      <div
        className={cn(
          PAGE_GUTTER_CLASS,
          "tw:pt-6 tw:lg:pt-[72px] tw:pb-10 tw:lg:pb-[72px]"
        )}
      >
        <div className="tw:flex tw:flex-col tw:lg:flex-row tw:gap-5 tw:lg:gap-6">
          <ServiceHeroCard
            className="tw:lg:flex-1"
            image={SERVICE_IMAGES.chauffeur.hero}
            eyebrow={t("servicesPage.Chauffeur.Kicker")}
            title={t("servicesPage.Chauffeur.Title")}
            description={t("servicesPage.Chauffeur.Intro")}
            ctaLabel={t("servicesPage.Chauffeur.BookButton")}
            ctaTo={buildLocalizedPath(i18n.language, "/book")}
          />
          <AsideNote
            className="tw:lg:w-[335.5px] tw:lg:shrink-0"
            title={t("servicesPage.Chauffeur.ServiceAreaNote.Title")}
            bodyKey="servicesPage.Chauffeur.ServiceAreaNote.Body"
          />
        </div>

        <Reveal className="tw:mt-5 tw:lg:mt-6 tw:bg-[#F6F4F0] tw:rounded-[8px] tw:p-5 tw:lg:p-8 tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center tw:gap-4 tw:lg:gap-[88px]">
          <div className="tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[15px] tw:leading-[22px] tw:lg:whitespace-nowrap">
            {t("servicesPage.Chauffeur.ExploreTransfers")}
          </div>
          <Stagger className="tw:flex-1 tw:grid tw:grid-cols-1 tw:lg:grid-cols-3 tw:gap-3">
            {TRANSFER_JUMP_LINKS.map((link) => (
              <StaggerItem key={link.id}>
                <QuickLinkCard
                  layout="centered"
                  icon={link.icon}
                  label={t(`servicesPage.Chauffeur.${link.id}.Title`)}
                  destination="section"
                  onClick={() => scrollToSection(link.sectionId)}
                />
              </StaggerItem>
            ))}
          </Stagger>
        </Reveal>
      </div>

      {/* Airport Transfer */}
      <ServiceSection id={CHAUFFEUR_SECTION_IDS.airportTransfer}>
        <Reveal>
          <SectionHeading
            kicker={t("servicesPage.Chauffeur.Kicker")}
            title={t("servicesPage.Chauffeur.AirportTransfer.Title")}
            intro={t("servicesPage.Chauffeur.AirportTransfer.Intro")}
          />
        </Reveal>
        <Reveal delay={0.08}>
          <ImagePair
            className={BLOCK_GAP_CLASS}
            images={SERVICE_IMAGES.chauffeur.airportTransferPair.map((image) => ({
              ...image,
              alt: t("servicesPage.Chauffeur.AirportTransfer.Title"),
            }))}
          />
        </Reveal>

        <Reveal>
          <LabeledSplit
            className={BLOCK_GAP_CLASS}
            label={t("servicesPage.Chauffeur.AirportTransfer.Pickup.Title")}
          >
            <RichText
              i18nKey="servicesPage.Chauffeur.AirportTransfer.Pickup.Description"
              className={BODY_TEXT_CLASS}
            />
            <Stagger className="tw:mt-8 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-3">
              {PICKUP_POINT_LINKS.map((link) => (
                <StaggerItem key={link.id}>
                  <QuickLinkCard
                    layout="stacked"
                    icon={link.icon}
                    label={t(
                      `servicesPage.Chauffeur.AirportTransfer.PickupPoints.${link.id}`
                    )}
                    destination="page"
                    to={buildLocalizedPath(
                      i18n.language,
                      `/Maps?type=${link.mapTab}`
                    )}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          </LabeledSplit>
        </Reveal>

        <div className={cn(DIVIDED_BLOCK_CLASS, COPY_WITH_PHOTO_ROW_CLASS)}>
          <Reveal className={cn("tw:lg:shrink-0", WIDE_COLUMN_CLASS)}>
            <BlockHeading
              title={t("servicesPage.Chauffeur.AirportTransfer.MeetAssist.Title")}
            >
              <RichText
                i18nKey="servicesPage.Chauffeur.AirportTransfer.MeetAssist.Description"
                className={BODY_TEXT_CLASS}
              />
            </BlockHeading>
            <ExpandableNote
              className="tw:mt-6 tw:lg:mt-8"
              title={t(
                "servicesPage.Chauffeur.AirportTransfer.MeetServiceNote.Title"
              )}
              body={t(
                "servicesPage.Chauffeur.AirportTransfer.MeetServiceNote.Body"
              )}
            />
          </Reveal>
          <StretchedSidePhoto
            image={SERVICE_IMAGES.chauffeur.meetAssist}
            alt={t("servicesPage.Chauffeur.AirportTransfer.MeetAssist.Title")}
          />
        </div>

        {/* Desktop: the three stacked cards take 800 of 1273px, the
            vehicle-comfort card fills the rest and matches their height. */}
        <div
          className={cn(
            DIVIDED_BLOCK_CLASS,
            "tw:flex tw:flex-col tw:lg:flex-row tw:gap-4"
          )}
        >
          <Stagger className="tw:flex tw:flex-col tw:gap-4 tw:lg:w-[62.85%] tw:lg:shrink-0">
            {AIRPORT_INFO_BLOCKS.map((blockId) => (
              <StaggerItem key={blockId}>
                <InfoCard
                  title={t(
                    `servicesPage.Chauffeur.AirportTransfer.Blocks.${blockId}.Title`
                  )}
                >
                  {t(
                    `servicesPage.Chauffeur.AirportTransfer.Blocks.${blockId}.Description`
                  )}
                </InfoCard>
              </StaggerItem>
            ))}
          </Stagger>

          {/* `tw:h-full` on both wrapper and card keeps this column as tall as
              the stack beside it, which pushes the photo and link down. */}
          <Reveal className="tw:lg:flex-1 tw:h-full" delay={0.1}>
            <InfoCard
              className="tw:h-full tw:flex tw:flex-col"
              contentClassName="tw:flex-1 tw:flex tw:flex-col tw:justify-between tw:gap-8"
              title={t(
                "servicesPage.Chauffeur.AirportTransfer.Blocks.VehicleComfort.Title"
              )}
            >
              <RichText
                i18nKey="servicesPage.Chauffeur.AirportTransfer.Blocks.VehicleComfort.Description"
                paragraphGapClass="tw:[&_p+p]:mt-[3px]!"
              />
              <div className="tw:flex tw:flex-col tw:gap-8">
                <img
                  src={SERVICE_IMAGES.chauffeur.fleetLineup.src}
                  style={getImageFocusStyle(SERVICE_IMAGES.chauffeur.fleetLineup)}
                  alt={t("servicesPage.Chauffeur.AirportTransfer.FleetsLink")}
                  className={cn(
                    "tw:block! tw:w-full tw:max-w-[300px] tw:h-[150px] tw:mx-auto tw:object-contain",
                    IMAGE_FOCUS_CLASS
                  )}
                />
                <Link
                  to={buildLocalizedPath(i18n.language, "/fleet")}
                  className="tw:self-end tw:flex tw:items-center tw:gap-x-[10px] tw:!no-underline tw:group"
                >
                  <DirectionsCarIcon color={ICON_COLOR} size={20} />
                  <span className="tw:font-hanken-grotesk tw:font-bold tw:text-[15px] tw:leading-[22px] tw:!text-[#4E4E4E]">
                    {t("servicesPage.Chauffeur.AirportTransfer.FleetsLink")}
                  </span>
                  {/* The arrow nudges right on hover, hinting where the link goes. */}
                  <span className="tw:flex tw:transition tw:duration-200 tw:group-hover:translate-x-1">
                    <ArrowRightIcon color="#C9C9C9" size={16} />
                  </span>
                </Link>
              </div>
            </InfoCard>
          </Reveal>
        </div>
      </ServiceSection>

      {/* Business Transfer */}
      <ServiceSection id={CHAUFFEUR_SECTION_IDS.businessTransfer}>
        <Reveal>
          <SectionHeading
            kicker={t("servicesPage.Chauffeur.Kicker")}
            title={t("servicesPage.Chauffeur.BusinessTransfer.Title")}
            intro={t("servicesPage.Chauffeur.BusinessTransfer.Intro")}
          />
        </Reveal>
        <Reveal delay={0.08}>
          <ImagePair
            className={BLOCK_GAP_CLASS}
            images={SERVICE_IMAGES.chauffeur.businessTransferPair.map((image) => ({
              ...image,
              alt: t("servicesPage.Chauffeur.BusinessTransfer.Title"),
            }))}
          />
        </Reveal>

        <Reveal>
          <LabeledSplit
            className={BLOCK_GAP_CLASS}
            label={t("servicesPage.Chauffeur.BusinessTransfer.Tailored.Title")}
          >
            <RichText
              i18nKey="servicesPage.Chauffeur.BusinessTransfer.Tailored.Description"
              className={BODY_TEXT_CLASS}
            />
            <InfoCard
              className="tw:mt-8"
              titleStyle="highlight"
              title={t(
                "servicesPage.Chauffeur.BusinessTransfer.Blocks.Golf.Title"
              )}
            >
              {t(
                "servicesPage.Chauffeur.BusinessTransfer.Blocks.Golf.Description"
              )}
            </InfoCard>
          </LabeledSplit>
        </Reveal>

        <div className={cn(DIVIDED_BLOCK_CLASS, COPY_WITH_PHOTO_ROW_CLASS)}>
          <Reveal className={cn("tw:lg:shrink-0", WIDE_COLUMN_CLASS)}>
            <BlockHeading title={t("servicesPage.Chauffeur.Concierge.Title")}>
              <div className={BODY_TEXT_CLASS}>
                {t("servicesPage.Chauffeur.Concierge.Intro")}
              </div>
            </BlockHeading>
            <Stagger className="tw:mt-6 tw:lg:mt-8 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
              {CONCIERGE_NOTES.map((noteId) => (
                <StaggerItem key={noteId} className="tw:h-full">
                  <div className="tw:h-full tw:bg-[#F6F4F0] tw:rounded-[8px] tw:p-6 tw:lg:p-8 tw:font-hanken-grotesk tw:text-[#4E4E4E] tw:text-[14px] tw:leading-[20px]">
                    <strong className="tw:font-bold tw:text-[#9A7B3F]">
                      {t(`servicesPage.Chauffeur.Concierge.${noteId}.Label`)}
                    </strong>{" "}
                    -{" "}
                    {t(`servicesPage.Chauffeur.Concierge.${noteId}.Description`)}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </Reveal>
          <StretchedSidePhoto
            image={SERVICE_IMAGES.chauffeur.concierge}
            alt={t("servicesPage.Chauffeur.Concierge.Title")}
          />
        </div>
      </ServiceSection>

      {/* Private Tour */}
      <ServiceSection id={CHAUFFEUR_SECTION_IDS.privateTour}>
        <Reveal>
          <SectionHeading
            kicker={t("servicesPage.Chauffeur.Kicker")}
            title={t("servicesPage.Chauffeur.PrivateTour.Title")}
            intro={t("servicesPage.Chauffeur.PrivateTour.Intro")}
          />
        </Reveal>
        <Reveal delay={0.08}>
          <ImagePair
            className={BLOCK_GAP_CLASS}
            images={SERVICE_IMAGES.chauffeur.privateTourPair.map((image) => ({
              ...image,
              alt: t("servicesPage.Chauffeur.PrivateTour.Title"),
            }))}
          />
        </Reveal>

        <Reveal>
          <LabeledSplit
            className={BLOCK_GAP_CLASS}
            label={t("servicesPage.Chauffeur.PrivateTour.Customized.Title")}
          >
            <div className={BODY_TEXT_CLASS}>
              {t("servicesPage.Chauffeur.PrivateTour.Customized.Description")}
            </div>
            <Stagger className="tw:mt-8 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-3">
              {PRIVATE_TOUR_LINKS.map((link) => (
                <StaggerItem key={link.id}>
                  <QuickLinkCard
                    icon={link.icon}
                    label={t(
                      `servicesPage.Chauffeur.PrivateTour.Links.${link.id}`
                    )}
                    destination="external"
                    href={link.href}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          </LabeledSplit>
        </Reveal>
      </ServiceSection>
    </div>
  );
};
